import { useEffect, useMemo, useState } from "react";
import { Skeleton, Dropdown, Drawer } from "../../components/ui";
import DataTableCards from "../../components/DataTable/DataTableCards";
import {
  Search,
  Filter,
  Plus,
  MoreHorizontal,
  ChevronLeft,
  ChevronRight,
  Image as ImageIcon,
  SearchX,
  Sparkles,
  Check,
  ChevronDown,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  useDeleteAdvertisement,
  useGetAllAdvertisements,
  useUpdateAdvertisement,
} from "../../query/advertisementQuery";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { UPLOADS_URL } from "../../config/constants";
import FilterSelect from "../../components/FilterSelect";
import swal from "../../utils/swal";
import "../teachers/teachers.css";
import "./advertisements.css";

const STATUSES = [
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" },
];

function statusTone(status) {
  return status === "ACTIVE" ? "is-active" : "is-inactive";
}

function statusLabel(status) {
  return status === "ACTIVE" ? "Active" : "Inactive";
}

function pageWindow(current, totalPages) {
  if (totalPages <= 3) return Array.from({ length: totalPages }, (_, i) => i + 1);
  if (current <= 2) return [1, 2, 3];
  if (current >= totalPages - 1) return [totalPages - 2, totalPages - 1, totalPages];
  return [current - 1, current, current + 1];
}

function buildStatusFormData(item, status) {
  const form = new FormData();
  form.append("title", item.title || "");
  form.append("link", item.link || "");
  form.append("order", String(item.order ?? 0));
  form.append("status", status);
  return form;
}

export default function Advertisements() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [paginationConfig, setPaginationConfig] = useState({
    pageNumber: 1,
    limit: "10",
    keyword: "",
    status: "",
  });
  const debouncedKeyword = useDebouncedValue(searchTerm.trim(), 400);

  const queryParams = useMemo(
    () => ({
      page: String(paginationConfig.pageNumber),
      limit: String(paginationConfig.limit),
      keyword: paginationConfig.keyword,
      status: paginationConfig.status,
    }),
    [paginationConfig]
  );

  const { isLoading, isFetching, data } = useGetAllAdvertisements(queryParams);
  const { mutate: deleteAd } = useDeleteAdvertisement();
  const { mutate: updateAd, isPending: isUpdating } = useUpdateAdvertisement();

  const docs = data?.docs || [];
  const pageSize = Number(paginationConfig.limit) || 10;
  const currentPage = Number(paginationConfig.pageNumber) || 1;
  const totalDocs = data?.totalDocs || 0;
  const totalPages = Math.max(1, Math.ceil(totalDocs / pageSize));
  const from = totalDocs === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const to = Math.min(currentPage * pageSize, totalDocs);
  const hasFilters = Boolean(paginationConfig.status);

  useEffect(() => {
    setPaginationConfig((prev) =>
      prev.keyword === debouncedKeyword
        ? prev
        : { ...prev, keyword: debouncedKeyword, pageNumber: 1 }
    );
  }, [debouncedKeyword]);

  useEffect(() => {
    if (!open) return;
    setSelectedStatus(paginationConfig.status || "");
  }, [open, paginationConfig.status]);

  const applyFilter = () => {
    setPaginationConfig((prev) => ({
      ...prev,
      pageNumber: 1,
      status: selectedStatus || "",
    }));
    setOpen(false);
  };

  const resetFilter = () => {
    setSelectedStatus("");
    setPaginationConfig((prev) => ({
      ...prev,
      pageNumber: 1,
      status: "",
    }));
    setOpen(false);
  };

  const handleStatus = (item, status) => {
    if (!item?._id || status === item.status) return;
    updateAd(
      { id: item._id, data: buildStatusFormData(item, status) },
      {
        onSuccess: () =>
          swal("Success", `Status updated to ${statusLabel(status)}`, "success"),
        onError: (error) =>
          swal("Error", error?.message || "Failed to update status", "error"),
      }
    );
  };

  const handleDelete = (item) => {
    swal({
      title: "Delete advertisement?",
      text: "This cannot be undone.",
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    }).then((ok) => {
      if (!ok) return;
      deleteAd(item._id, {
        onSuccess: () => swal("Deleted", "Advertisement removed", "success"),
        onError: (error) =>
          swal("Error", error?.message || "Failed to delete", "error"),
      });
    });
  };

  return (
    <div className="data-page advertisements-page">
      <div className="data-toolbar">
        <div className="data-toolbar-search">
          <label className="data-search">
            <Search size={16} />
            <input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search here..."
            />
            {isFetching && searchTerm ? (
              <span className="data-search-spinner" aria-hidden />
            ) : null}
          </label>
          <button
            type="button"
            className={`data-icon-btn${hasFilters ? " is-active" : ""}`}
            aria-label="Filter"
            onClick={() => setOpen(true)}
          >
            <Filter size={18} />
            {hasFilters ? <span className="data-filter-dot" /> : null}
          </button>
        </div>
        <div className="data-toolbar-actions">
          <button
            type="button"
            className="data-primary"
            onClick={() => navigate("/advertisements/add")}
          >
            <Plus size={18} />
            Add advertisement
          </button>
        </div>
      </div>

      <div className="data-card">
        {isLoading && !data ? (
          <div style={{ padding: 24 }}>
            <Skeleton active />
          </div>
        ) : docs.length === 0 ? (
          <div className="data-empty">
            <span className="data-empty-icon">
              {paginationConfig.keyword || hasFilters ? (
                <SearchX size={36} strokeWidth={1.6} />
              ) : (
                <Sparkles size={36} strokeWidth={1.6} />
              )}
            </span>
            <h3>
              {paginationConfig.keyword || hasFilters
                ? "No advertisements found"
                : "No advertisements yet"}
            </h3>
            <p>
              {paginationConfig.keyword || hasFilters
                ? "Try a different keyword or clear filters."
                : "Add a home-screen banner ad for the parent app."}
            </p>
            {!paginationConfig.keyword && !hasFilters ? (
              <button
                type="button"
                className="data-primary"
                onClick={() => navigate("/advertisements/add")}
              >
                <Plus size={16} />
                Add advertisement
              </button>
            ) : null}
          </div>
        ) : (
          <>
            <DataTableCards fetching={isFetching || isUpdating}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Ad</th>
                    <th>Link</th>
                    <th>Order</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {docs.map((item) => {
                    const title = item.title || "Untitled";
                    const imageSrc = item.image
                      ? `${UPLOADS_URL}/${item.image}`
                      : null;
                    const actionItems = [
                      {
                        key: "edit",
                        label: <div style={{ padding: "8px 12px" }}>Edit</div>,
                        onClick: () =>
                          navigate(`/advertisements/edit/${item._id}`),
                      },
                      {
                        key: "delete",
                        label: (
                          <div style={{ padding: "8px 12px", color: "#e83038" }}>
                            Delete
                          </div>
                        ),
                        onClick: () => handleDelete(item),
                      },
                    ];

                    return (
                      <tr key={item._id}>
                        <td>
                          <div className="ad-title-cell">
                            {imageSrc ? (
                              <span className="ad-thumb-wrap">
                                <img
                                  className="ad-thumb"
                                  src={imageSrc}
                                  alt={title}
                                  loading="lazy"
                                />
                              </span>
                            ) : (
                              <span className="ad-thumb-wrap is-empty">
                                <ImageIcon size={18} />
                              </span>
                            )}
                            <p className="data-name">{title}</p>
                          </div>
                        </td>
                        <td>
                          <p className="ad-link-snippet">
                            {item.link ? item.link : "—"}
                          </p>
                        </td>
                        <td>{item.order ?? 0}</td>
                        <td>
                          <Dropdown
                            menu={{
                              items: STATUSES.map((option) => ({
                                key: option.value,
                                label: (
                                  <div className="data-status-option">
                                    <span
                                      className={`data-status-dot ${statusTone(
                                        option.value
                                      )}`}
                                    />
                                    {option.label}
                                    {item.status === option.value ? (
                                      <Check size={14} />
                                    ) : null}
                                  </div>
                                ),
                                onClick: () =>
                                  handleStatus(item, option.value),
                              })),
                            }}
                            trigger={["click"]}
                            placement="bottomLeft"
                          >
                            <button
                              type="button"
                              className={`data-pill data-status-trigger ${statusTone(
                                item.status
                              )}`}
                              aria-label="Change status"
                              disabled={isUpdating}
                            >
                              {statusLabel(item.status)}
                              <ChevronDown size={12} />
                            </button>
                          </Dropdown>
                        </td>
                        <td>
                          <Dropdown
                            menu={{ items: actionItems }}
                            trigger={["click"]}
                            placement="bottomRight"
                          >
                            <button
                              type="button"
                              className="data-action"
                              aria-label="More actions"
                            >
                              <MoreHorizontal size={20} />
                            </button>
                          </Dropdown>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </DataTableCards>

            <div className="data-pager">
              <p className="data-pager-text">
                Showing {from}-{to} from {totalDocs} data
              </p>
              <div className="data-pages">
                <button
                  type="button"
                  className="data-page-btn is-nav"
                  disabled={currentPage <= 1}
                  onClick={() =>
                    setPaginationConfig((prev) => ({
                      ...prev,
                      pageNumber: Math.max(1, currentPage - 1),
                    }))
                  }
                >
                  <ChevronLeft size={18} />
                </button>
                {pageWindow(currentPage, totalPages).map((page) => (
                  <button
                    key={page}
                    type="button"
                    className={`data-page-btn${
                      page === currentPage ? " is-active" : ""
                    }`}
                    onClick={() =>
                      setPaginationConfig((prev) => ({
                        ...prev,
                        pageNumber: page,
                      }))
                    }
                  >
                    {page}
                  </button>
                ))}
                <button
                  type="button"
                  className="data-page-btn is-nav"
                  disabled={currentPage >= totalPages}
                  onClick={() =>
                    setPaginationConfig((prev) => ({
                      ...prev,
                      pageNumber: Math.min(totalPages, currentPage + 1),
                    }))
                  }
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        placement="right"
        title="Filter"
        width={400}
        footer={
          <>
            <button type="button" className="bw-drawer-btn is-ghost" onClick={resetFilter}>
              Clear All
            </button>
            <button type="button" className="bw-drawer-btn is-primary" onClick={applyFilter}>
              Apply
            </button>
          </>
        }
      >
        <div className="bw-filter-field">
          <p className="bw-filter-label">Status</p>
          <FilterSelect
            multiple
            value={selectedStatus}
            onChange={setSelectedStatus}
            options={[
              { value: "", label: "All statuses" },
              ...STATUSES,
            ]}
          />
        </div>
      </Drawer>
    </div>
  );
}
